function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import { Menu, useOverlayContext } from 'seagull';
import { AddCircleLiner24 } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import ServiceBuilder from "../../ServiceBuilder/ServiceBuilder";
import { defaultBillingDayOfWeek } from "../../../constants";
import { useActiveServiceTemplates } from "../../../hooks/reactQuery/queries/useActiveServiceTemplates";
import { useServiceTemplateApi } from "../../../hooks/useServiceTemplateApi";
import { Intent } from "../../../infra/access-control";
import { handlePromise } from "../../../infra/utils";
import { BillingDayOfMonthModel, serviceCostModel, serviceModel, serviceTemplateModel } from "../../../models";
import { selectors } from "../../../store/state-selectors";
import { useStoreStatic } from "../../../store/store-context";
import { StartBillingOnEnum } from "../../../types/api.model";
import { NewServiceType, SelectNewServiceTypeModal } from "../SelectNewServiceTypeModal/SelectNewServiceTypeModal";
import { jsx as _jsx } from "react/jsx-runtime";
export function serviceFromServiceTemplate(_ref) {
  let {
    serviceTemplate,
    agreementData,
    businessSettings
  } = _ref;
  const skipBillingOnAcceptance = agreementData.isActiveAgreement() || (businessSettings !== null && businessSettings !== void 0 && businessSettings.billUpfront ? businessSettings.startBillingOn === StartBillingOnEnum.SkipBillingOnAcceptance : true);
  return serviceModel.fromServiceTemplate(serviceTemplate, agreementData.serviceSource(), {
    isBilledUpfront: (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billUpfront) || false,
    skipBillingOnAcceptance,
    billingDayOfWeek: (businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billingDayOfWeek) || defaultBillingDayOfWeek,
    billingDayOfMonth: BillingDayOfMonthModel.make(businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.billingDayOfMonth),
    prorateFirstInvoice: businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.prorateFirstInvoice,
    isPaused: businessSettings === null || businessSettings === void 0 ? void 0 : businessSettings.automaticServicesPausedByDefault
  });
}
export function formatServiceSubtext(s) {
  const isBundle = serviceTemplateModel.isBundle(s);
  const subtextFirstPart = isBundle ? serviceTemplateModel.formatNumIncludedServices(s) : serviceCostModel.stringifyServiceCost(s.cost).costDisplay;
  const subtextSecondPart = serviceTemplateModel.formatExtendedRecurrencePeriod(s, false);
  return "".concat(subtextFirstPart, " \u2022 ").concat(subtextSecondPart);
}
export const GetAddServicesPopoverTID = parentTestId => getTestIds(parentTestId);
export function AddServicesPopover(_ref2) {
  let {
    testId,
    agreementData,
    onSelectServiceTemplate
  } = _ref2;
  const {
    AddServicesPopoverTID
  } = useMemo(() => GetAddServicesPopoverTID(testId), [testId]);
  const {
    getState
  } = useStoreStatic();
  const {
    data: serviceTemplates,
    isLoading,
    refresh
  } = useActiveServiceTemplates({
    refreshOnMount: true
  });
  const serviceTemplateApi = useServiceTemplateApi();
  const {
    showModal,
    startLoader,
    stopLoader,
    showErrorToast
  } = useOverlayContext();
  function selectServiceTemplate(serviceTemplate) {
    const service = serviceFromServiceTemplate({
      serviceTemplate,
      agreementData,
      businessSettings: selectors.user.businessSettings(getState())
    });
    onSelectServiceTemplate(service);
  }
  async function createNewServiceTemplate() {
    const newServiceType = await showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/_jsx(SelectNewServiceTypeModal, _objectSpread({}, p))
    });
    if (!newServiceType) return;
    showModal({
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/_jsx(ServiceBuilder, _objectSpread({
        serviceNameBlacklist: serviceTemplates ? serviceTemplates.map(s => s.name) : [],
        agreementData: agreementData,
        serviceOrigin: agreementData.isActiveAgreement() ? 'agreement' : 'draft',
        isCreatingBundle: newServiceType === NewServiceType.Bundle,
        onSave: async output => {
          const body = serviceTemplateModel.fromService(output);
          const loaderName = 'createNewServiceLoader';
          startLoader(loaderName);
          const result = await handlePromise(serviceTemplateApi.create(body));
          if (result.success) {
            await refresh({
              throwOnError: false
            });
            const newServiceTemplate = serviceTemplateModel.setId(body, result.data.id);
            selectServiceTemplate(newServiceTemplate);
          } else {
            showErrorToast('Failed to create service', AddServicesPopoverTID.ErrorToast());
          }
          stopLoader(loaderName);
          return result;
        }
      }, p))
    });
  }
  return /*#__PURE__*/_jsx(Menu, {
    dimensions: {
      height: 344
    },
    loading: isLoading,
    emptyText: "No matches found. Try again or create a new service.",
    searchProps: {
      showSearch: true,
      autoFocus: true,
      strategy: {
        type: 'text'
      }
    },
    testId: AddServicesPopoverTID.Menu(),
    items: (serviceTemplates || []).map(s => ({
      id: s.id,
      title: s.name,
      body: formatServiceSubtext(s),
      badge: serviceTemplateModel.isBundle(s) ? {
        text: 'Bundle'
      } : undefined,
      onClick: () => selectServiceTemplate(s)
    })),
    footer: {
      items: [{
        variant: 'contextualAction',
        props: {
          title: 'Create new',
          intent: Intent.CreateServiceTemplate,
          icon: /*#__PURE__*/_jsx(AddCircleLiner24, {}),
          testId: AddServicesPopoverTID.CreateNew(),
          onClick: createNewServiceTemplate
        }
      }]
    }
  });
}